import { enhancedApiClient, isSuccessResponse, ApiResponse } from "../../services/enhancedApiClient";
import { Order, ItemStatus } from "./order";
import { USE_STUB_DATA, STUB_ORDERS, STUB_CUSTOMERS } from "../../config/stubData";
import { v4 as uuidv4 } from "uuid";

// In-memory store for stub data
let orders = [...STUB_ORDERS];

class OrderService {
  // Get all orders with pagination and filtering
  async getOrders(params?: {
    page?: number;
    limit?: number;
    status?: string;
    priority?: string;
    search?: string;
    siteId?: string;
  }): Promise<ApiResponse<{ orders: Order[]; pagination: any }>> {
    if (USE_STUB_DATA) {
      // Type assertion for stub data to match Order interface
      let stubOrders = orders as unknown as Order[];
      
      // Filter by siteId if provided
      if (params?.siteId) {
        stubOrders = stubOrders.filter((order: any) => 
          order.site_id === params.siteId || 
          order.siteId === params.siteId || 
          order.site?.id === params.siteId
        );
      }
      
      return Promise.resolve({
        success: true,
        data: {
          orders: stubOrders,
          pagination: { total: stubOrders.length, page: 1, limit: 10 },
        },
      });
    }
    
    const response = await enhancedApiClient.get("/orders", { 
      params,
      context: "Fetch Orders",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get order by ID with enhanced details
  async getOrder(orderId: string): Promise<ApiResponse<Order>> {
    if (USE_STUB_DATA) {
      const order = orders.find((o) => o.id === orderId);
      if (!order) {
        throw new Error("Order not found");
      }
      
      // Enhance order with detailed item and stage information
      const enhancedOrder = await this.enhanceOrderWithDetails(order as unknown as Order);
      return Promise.resolve({ success: true, data: enhancedOrder });
    }
    
    const response = await enhancedApiClient.get(`/orders/${orderId}`, {
      context: "Fetch Order By ID",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      // Enhance the response with detailed information
      const enhancedOrder = await this.enhanceOrderWithDetails(response.data);
      return { success: true, data: enhancedOrder };
    }
    
    return response;
  }

  // Enhance order with detailed item and stage information
  private async enhanceOrderWithDetails(order: Order): Promise<Order> {
    const allItems: any[] = [];
    
    // Collect all items from all products
    order.products.forEach(product => {
      if (product.frame) allItems.push(...product.frame);
      if (product.panel) allItems.push(...product.panel);
      if (product.panelWindow) allItems.push(...product.panelWindow);
      if (product.net) allItems.push(...product.net);
      if (product.openableType) allItems.push(...product.openableType);
      if (product.items) allItems.push(...product.items);
    });

    // Process each item to get detailed stage information
    const orderItemDetails = allItems.map(item => {
      const stages = item.stages || [];
      const logs = item.logs || [];
      
      const completedStages = stages.filter((stage: any) => stage.status === 'completed').length;
      const inProgressStages = stages.filter((stage: any) => stage.status === 'in_progress').length;
      const pendingStages = stages.filter((stage: any) => stage.status === 'pending').length;
      const totalStages = stages.length;
      
      const progressPercentage = totalStages > 0 ? (completedStages / totalStages) * 100 : 0;

      // Map stages to detailed format
      const stageDetails = stages.map((stage: any) => {
        // Find the completion log entry for this stage
        const completionLog = logs.find((log: any) => 
          log.stage === stage.stage && 
          (log.status === 'completed' || log.status === 'COMPLETED')
        );
        
        // Find any log entry for this stage (for started_at)
        const anyLogEntry = logs.find((log: any) => log.stage === stage.stage);
        
        // Debug logging for API response
        if (stage.status === 'completed' && logs.length > 0) {
          console.log(`API Response - Item ${item.id} Stage ${stage.stage}:`, {
            stage,
            logs,
            completionLog,
            anyLogEntry,
            availableFields: completionLog ? Object.keys(completionLog) : 'No completion log',
            workerName: completionLog?.user_name,
            allLogsForStage: logs.filter(log => log.stage === stage.stage)
          });
        }
        
        return {
          id: stage.id || `${item.id}-stage-${stage.stage}`,
          stage: stage.stage,
          status: stage.status as 'pending' | 'in_progress' | 'completed',
          order: stage.order || 0,
          startedAt: anyLogEntry?.started_at || stage.started_at,
          completedAt: completionLog?.completed_at || stage.completed_at,
          assignedTo: completionLog?.user_id || anyLogEntry?.user_id || stage.user_id,
          assignedToName: stage.user_name || completionLog?.user_name || anyLogEntry?.user_name,
          user_name: stage.user_name || completionLog?.user_name || anyLogEntry?.user_name,
          estimatedHours: stage.estimated_hours || 0,
          actualHours: completionLog?.duration_hours || stage.actual_hours || 0,
          notes: completionLog?.notes || stage.notes,
          requiresQrScan: stage.requires_qr_scan || false,
          requiresPhoto: stage.requires_photo || false,
          requiresSupervisorApproval: stage.requires_supervisor_approval || false,
        };
      });

      // Map logs to stage history
      const stageHistory = logs.map((log: any) => ({
        id: log.id,
        stage: log.stage,
        previousStage: log.previous_stage,
        status: log.status,
        userId: log.user_id,
        userName: log.user_name,
        timestamp: log.timestamp,
        notes: log.notes,
        photoUrl: log.photo_url,
        location: log.location,
        durationHours: log.duration_hours,
        qualityCheck: log.quality_check,
        metadata: log.metadata,
      }));

      return {
        id: item.id,
        itemNumber: item.itemNumber || item.item_tracking_code,
        number: item.number,
        type: item.type,
        width: item.width || item.specifications?.width || '0',
        height: item.height || item.specifications?.height || '0',
        status: item.status,
        currentStage: item.current_stage || 'Not Started',
        assignedTo: item.assigned_to,
        assignedToName: item.assigned_to_name || 'Unknown',
        totalStages,
        completedStages,
        inProgressStages,
        pendingStages,
        progressPercentage,
        stages: stageDetails,
        stageHistory,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
      };
    });

    // Create enhanced order with detailed information
    const enhancedOrder: Order = {
      ...order,
      orderDetails: {
        totalItemsInOrder: allItems.length,
        items: orderItemDetails,
      },
    };

    return enhancedOrder;
  }

  // Create new order
  async createOrder(orderData: Partial<Order>): Promise<ApiResponse<Order>> {
    if (USE_STUB_DATA) {
      const newOrder: Order = {
        ...orderData,
        id: Math.random().toString(),
        createdAt: new Date(),
        updatedAt: new Date(),
      } as Order;
      orders.push(newOrder as any);
      return Promise.resolve({ success: true, data: newOrder });
    }
    
    // Backend now accepts the new itemTypes format directly
    const response = await enhancedApiClient.post("/orders", orderData, {
      context: "Create Order",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Update order
  async updateOrder(
    orderId: string,
    orderData: Partial<Order>
  ): Promise<ApiResponse<Order>> {
    if (USE_STUB_DATA) {
      const index = orders.findIndex((o) => o.id === orderId);
      if (index === -1) {
        throw new Error("Order not found");
      }
      orders[index] = { ...orders[index], ...orderData, updatedAt: new Date() } as any;
      return Promise.resolve({ success: true, data: orders[index] as unknown as Order });
    }
    
    // Backend now accepts the new itemTypes format directly
    const response = await enhancedApiClient.put(`/orders/${orderId}`, orderData, {
      context: "Update Order",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Delete order
  async deleteOrder(orderId: string): Promise<ApiResponse<{ message: string }>> {
    if (USE_STUB_DATA) {
      const index = orders.findIndex((o) => o.id === orderId);
      if (index === -1) {
        throw new Error("Order not found");
      }
      orders.splice(index, 1);
      return Promise.resolve({ success: true, data: { message: "Order deleted successfully" } });
    }
    
    const response = await enhancedApiClient.delete(`/orders/${orderId}`, {
      context: "Delete Order",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Update order status
  async updateOrderStatus(
    orderId: string,
    status: ItemStatus
  ): Promise<ApiResponse<Order>> {
    if (USE_STUB_DATA) {
      const order = orders.find((o) => o.id === orderId);
      if (!order) {
        throw new Error("Order not found");
      }
      (order as any).status = status;
      (order as any).updatedAt = new Date();
      return Promise.resolve({ success: true, data: order as unknown as Order });
    }
    
    const response = await enhancedApiClient.patch(`/orders/${orderId}/status`, {
      status,
    }, {
      context: "Update Order Status",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get orders by status
  async getOrdersByStatus(status: string): Promise<ApiResponse<Order[]>> {
    if (USE_STUB_DATA) {
      const filteredOrders = orders.filter((o) => (o as any).status === status);
      return Promise.resolve({ success: true, data: filteredOrders as unknown as Order[] });
    }
    
    const response = await enhancedApiClient.get(`/orders?status=${status}`, {
      context: "Get Orders By Status",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get orders by customer
  async getOrdersByCustomer(customerId: string): Promise<ApiResponse<Order[]>> {
    if (USE_STUB_DATA) {
      const filteredOrders = orders.filter((o) => (o as any).customerId === customerId);
      return Promise.resolve({ success: true, data: filteredOrders as unknown as Order[] });
    }
    
    const response = await enhancedApiClient.get(`/orders?customer_id=${customerId}`, {
      context: "Get Orders By Customer",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Search orders
  async searchOrders(searchTerm: string): Promise<ApiResponse<Order[]>> {
    if (USE_STUB_DATA) {
      const filteredOrders = orders.filter((o) =>
        (o as any).orderNumberNew?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (o as any).orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (o as any).customerName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      return Promise.resolve({ success: true, data: filteredOrders as unknown as Order[] });
    }
    
    const response = await enhancedApiClient.get(`/orders?search=${searchTerm}`, {
      context: "Search Orders",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get order statistics
  async getOrderStats(): Promise<ApiResponse<any>> {
    if (USE_STUB_DATA) {
      const stats = {
        total: orders.length,
        pending: orders.filter((o) => (o as any).status === "pending").length,
        inProgress: orders.filter((o) => (o as any).status === "in_progress").length,
        completed: orders.filter((o) => (o as any).status === "completed").length,
        cancelled: orders.filter((o) => (o as any).status === "cancelled").length,
      };
      return Promise.resolve({ success: true, data: stats });
    }
    
    const response = await enhancedApiClient.get("/orders/statistics", {
      context: "Get Order Statistics",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }


  // Get order timeline
  async getOrderTimeline(orderId: string): Promise<ApiResponse<any[]>> {
    if (USE_STUB_DATA) {
      const order = orders.find((o) => o.id === orderId);
      if (!order) {
        throw new Error("Order not found");
      }
      const timeline = [
        { timestamp: (order as any).createdAt, action: "Order Created", status: "pending" },
        { timestamp: (order as any).updatedAt, action: "Order Updated", status: (order as any).status },
      ];
      return Promise.resolve({ success: true, data: timeline });
    }
    
    const response = await enhancedApiClient.get(`/orders/${orderId}/timeline`, {
      context: "Get Order Timeline",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Export orders
  async exportOrders(params?: {
    format?: string;
    filters?: any;
  }): Promise<ApiResponse<any>> {
    if (USE_STUB_DATA) {
      return Promise.resolve({ success: true, data: { message: "Export not available in stub mode" } });
    }
    
    const response = await enhancedApiClient.get("/orders/export", {
      params,
      context: "Export Orders",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Bulk update orders
  async bulkUpdateOrders(
    orderIds: string[],
    updates: Partial<Order>
  ): Promise<ApiResponse<{ message: string; updatedCount: number }>> {
    if (USE_STUB_DATA) {
      let updatedCount = 0;
      orderIds.forEach((id) => {
        const order = orders.find((o) => o.id === id);
        if (order) {
          Object.assign(order, updates, { updatedAt: new Date() });
          updatedCount++;
        }
      });
      return Promise.resolve({
        success: true,
        data: { message: `${updatedCount} orders updated successfully`, updatedCount },
      });
    }
    
    const response = await enhancedApiClient.patch("/orders/bulk-update", {
      orderIds,
      updates,
    }, {
      context: "Bulk Update Orders",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Create new order with the updated data structure
  async createOrderWithItemTypes(orderData: any): Promise<ApiResponse<{ order: any }>> {
   
    // Real API call
    const response = await enhancedApiClient.post("/orders", orderData, {
      context: "Create Order",
      component: "OrderService"
    });

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  // Update order with the new data structure
  async updateOrderWithItemTypes(orderId: string, orderData: any): Promise<ApiResponse<{ order: any }>> {


    // Real API call
    const response = await enhancedApiClient.put(`/orders/${orderId}`, orderData, {
      context: "Update Order",
      component: "OrderService"
    });

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  // Helper method to calculate total items from itemTypes
  private calculateTotalItems(itemTypes: Record<string, any[]>): number {
    if (!itemTypes) return 0;
    return Object.values(itemTypes).reduce((sum, items) => sum + items.length, 0);
  }

  // Add product to existing order (incremental approach)
  async addProductToOrder(
    orderId: string,
    productData: {
      name: string;
      type: string;
      evaOrder?: string;
      label?: string;
      location?: string;
      notes?: string;
      description?: string;
      color?: string;
      specifications?: any;
      itemTypes: Record<string, any[]>;
    }
  ): Promise<ApiResponse<{ product: any; message: string }>> {
    if (USE_STUB_DATA) {
      // For stub data, simulate adding product to existing order
      const order = orders.find((o) => o.id === orderId);
      if (!order) {
        throw new Error("Order not found");
      }
      
      const newProduct = {
        id: uuidv4(),
        name: productData.name,
        type: productData.type,
        items: this.flattenItemTypes(productData.itemTypes),
        totalItems: this.calculateTotalItems(productData.itemTypes),
        completedItems: 0,
      };
      
      (order as any).products = [...((order as any).products || []), newProduct];
      (order as any).totalItems = ((order as any).totalItems || 0) + newProduct.totalItems;
      (order as any).updatedAt = new Date();
      
      return Promise.resolve({
        success: true,
        data: {
          product: newProduct,
          message: "Product added successfully",
        },
      });
    }
    
    const response = await enhancedApiClient.post(`/orders/${orderId}/products`, productData, {
      context: "Add Product to Order",
      component: "OrderService",
      retryAttempts: 2, // Reduced retries for product operations
      retryDelay: 3000, // Longer delay between retries for complex operations
      timeout: 90000 // 90 seconds timeout for product operations
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Create order with first product only (incremental approach)
  async createOrderWithFirstProduct(orderData: {
    customerId: string;
    siteId?: string;
    totalAmount?: number;
    priority?: string;
    dueDate?: string;
    notes?: string;
    orderLabel?: string;
    orderNotes?: string;
    paymentStatus?: string;
    firstProduct: {
      name: string;
      type: string;
      evaOrder?: string;
      label?: string;
      location?: string;
      notes?: string;
      color?:string;
      description?: string;
      specifications?: any;
      itemTypes: Record<string, any[]>;
    };
  }): Promise<ApiResponse<{ order: any; message: string }>> {
    // Create order with only the first product
    const orderPayload = {
      customerId: orderData.customerId,
      siteId: orderData.siteId,
      totalAmount: orderData.totalAmount,
      priority: orderData.priority,
      dueDate: orderData.dueDate,
      notes: orderData.notes,
      orderLabel: orderData.orderLabel,
      orderNotes: orderData.orderNotes,
      paymentStatus: orderData.paymentStatus,
      products: [orderData.firstProduct],
    };
    
    const response = await enhancedApiClient.post("/orders", orderPayload, {
      context: "Create Order with First Product",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }


  // Helper method to flatten itemTypes structure for display
  private flattenItemTypes(itemTypes: Record<string, any[]>): any[] {
    if (!itemTypes) return [];
    return Object.entries(itemTypes).flatMap(([type, items]) =>
      items.map(item => ({
        ...item,
        type,
        id: item.id || uuidv4(),
      }))
    );
  }

  // Update existing product in order
  async updateProductInOrder(
    orderId: string,
    productId: string,
    productData: {
      name: string;
      type: string;
      color: string;
      evaOrder?: string;
      label?: string;
      location?: string;
      notes?: string;
      description?: string;
      specifications?: any;
      itemTypes: Record<string, any[]>;
    }
  ): Promise<ApiResponse<{ product: any; message: string }>> {
 
    
    const response = await enhancedApiClient.put(`/orders/${orderId}/products/${productId}`, productData, {
      context: "Update Product in Order",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Delete product from order
  async deleteProductFromOrder(
    orderId: string,
    productId: string
  ): Promise<ApiResponse<{ message: string }>> {
   
    
    const response = await enhancedApiClient.delete(`/orders/${orderId}/products/${productId}`, {
      context: "Delete Product from Order",
      component: "OrderService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Helper method to transform items for backend compatibility
  private transformItemsForBackend(items: any[], itemType: string, orderId: string, productId: string): any[] {
    return items.map((item: any) => ({
      id: uuidv4(),
      order_id: orderId,
      product_id: productId,
      qr_code: uuidv4(),
      qr_code_image_url: null,
      type: itemType,
      current_stage: "Production",
      current_stage_order: 0,
      status: "pending",
      specifications: {
        ...item.specifications,
        // Ensure width and height are preserved
        width: item.specifications.width,
        height: item.specifications.height,
        material: item.specifications.material,
        finish: item.specifications.finish
      },
      assigned_to: item.assigned_to || null,
      supervisor_id: null,
      started_at: null,
      completed_at: null,
      estimated_hours: item.estimated_hours,
      actual_hours: null,
      workflow_data: {},
      metadata: {},
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      quantity: item.quantity,
      deleted: false,
      deleted_at: null,
      deleted_by: null,
      logs: []
    }));
  }
}

export const orderService = new OrderService();
